import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  FormControlLabel,
  Switch,
  CircularProgress,
  Typography,
  Autocomplete,
  Chip,
  Box,
  Divider
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import { event as eventApi, user as userApi, racetrack as racetrackApi } from '../../../utils/api';
import { toast } from 'react-toastify';
import ImageUploader from '../../../components/shared/ImageUploader';
import { getTransformedImageUrl } from '../../../utils/filestack';

const CreateEventModal = ({ open, onClose, onSuccess }) => {
  const { t } = useTranslation('events');
  const [loading, setLoading] = useState(false);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [loadingRacetracks, setLoadingRacetracks] = useState(false);
  const [availableUsers, setAvailableUsers] = useState([]);
  const [availableRacetracks, setAvailableRacetracks] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [selectedRacetrack, setSelectedRacetrack] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    location: '',
    date: format(new Date(), 'yyyy-MM-dd'),
    endDate: '',
    price: 0,
    signupLink: '',
    maxParticipants: '',
    isPublic: true,
    isRaceday: false,
    imageUrl: ''
  });

  // Fetch users when the modal is opened and isPublic is false
  useEffect(() => {
    if (open && !formData.isPublic) {
      fetchUsers();
    }
  }, [open, formData.isPublic]);

  // Fetch racetracks when the modal is opened
  useEffect(() => {
    if (open) {
      fetchRacetracks();
    }
  }, [open]);

  // Update location when a racetrack is selected
  useEffect(() => {
    if (selectedRacetrack && formData.isRaceday) {
      setFormData(prev => ({
        ...prev,
        location: `${selectedRacetrack.name}, ${selectedRacetrack.location}, ${selectedRacetrack.country}`
      }));
    }
  }, [selectedRacetrack, formData.isRaceday]);

  const fetchUsers = async () => {
    try {
      setLoadingUsers(true);
      const users = await userApi.getUsers();
      setAvailableUsers(users);
    } catch (error) {
      console.error('Error fetching users:', error);
      toast.error(t('errors.loadingUsers'));
    } finally {
      setLoadingUsers(false);
    }
  };

  const fetchRacetracks = async () => {
    try {
      setLoadingRacetracks(true);
      const racetracks = await racetrackApi.getAll();
      setAvailableRacetracks(racetracks);
    } catch (error) {
      console.error('Error fetching racetracks:', error);
      toast.error('Error loading racetracks');
    } finally {
      setLoadingRacetracks(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      setLoading(true);
      
      // Format the data
      const eventData = {
        ...formData,
        price: formData.price ? Number(formData.price) : 0,
        maxParticipants: formData.maxParticipants ? Number(formData.maxParticipants) : null
      };
      
      // Add racetrack if selected and isRaceday is true
      if (formData.isRaceday && selectedRacetrack) {
        eventData.racetrack = selectedRacetrack._id;
      }
      
      // Add initial participants and invited users for private events
      if (!formData.isPublic && selectedUsers.length > 0) {
        // Add as participants
        eventData.initialParticipants = selectedUsers.map(user => ({
          userId: user._id,
          status: 'maybe'
        }));
        
        // Also add to invited users
        eventData.invitedUsers = selectedUsers.map(user => user._id);
      }
      
      // Debug: Log the data being sent
      console.log('Event data being sent:', JSON.stringify(eventData, null, 2));
      
      await eventApi.create(eventData);
      resetForm();
      onSuccess();
    } catch (error) {
      console.error('Error creating event:', error);
      console.error('Error response:', error.response?.data);
      toast.error(t('errors.creatingEvent'));
    } finally {
      setLoading(false);
    }
  };

  const handleImageUploadSuccess = (imageUrl) => {
    setFormData({
      ...formData,
      imageUrl
    });
  };

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      location: '',
      date: format(new Date(), 'yyyy-MM-dd'),
      endDate: '',
      price: 0,
      signupLink: '',
      maxParticipants: '',
      isPublic: true,
      isRaceday: false,
      imageUrl: ''
    });
    setSelectedUsers([]);
    setSelectedRacetrack(null);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{t('createEvent.title')}</DialogTitle>
      <form onSubmit={handleSubmit}>
        <DialogContent>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                name="title"
                label={t('createEvent.fields.title')}
                fullWidth
                required
                value={formData.title}
                onChange={handleChange}
              />
            </Grid>
            
            {/* Event Image Upload */}
            <Grid item xs={12}>
              <Typography variant="subtitle2" gutterBottom>
                {t('createEvent.fields.eventImage')}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <ImageUploader
                  onUploadSuccess={handleImageUploadSuccess}
                  buttonText={t('createEvent.fields.uploadImage')}
                  buttonProps={{ variant: 'contained', sx: { mr: 2 } }}
                />
                {formData.imageUrl && (
                  <Typography variant="body2" color="success.main">
                    {t('createEvent.fields.imageUploaded')}
                  </Typography>
                )}
              </Box>
              {formData.imageUrl && (
                <Box sx={{ mt: 2, mb: 3, maxWidth: 300 }}>
                  <img 
                    src={getTransformedImageUrl(formData.imageUrl, 'thumbnail')} 
                    alt={formData.title || 'Event preview'} 
                    style={{ width: '100%', borderRadius: 8 }}
                  />
                </Box>
              )}
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                name="description"
                label={t('createEvent.fields.description')}
                fullWidth
                required
                multiline
                rows={4}
                value={formData.description}
                onChange={handleChange}
              />
            </Grid>
            
            {/* Raceday section */}
            <Grid item xs={12}>
              <Divider sx={{ my: 1 }} />
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.isRaceday}
                    onChange={handleChange}
                    name="isRaceday"
                    color="primary"
                  />
                }
                label={t('createEvent.fields.isRaceday')}
              />
              <Typography variant="caption" color="text.secondary" display="block" mt={1}>
                {t('createEvent.fields.raceday.description')}
              </Typography>
            </Grid>
            
            {formData.isRaceday && (
              <Grid item xs={12}>
                <Autocomplete
                  id="racetrack-selection"
                  options={availableRacetracks}
                  loading={loadingRacetracks}
                  value={selectedRacetrack}
                  onChange={(event, newValue) => setSelectedRacetrack(newValue)}
                  getOptionLabel={(option) => `${option.name}, ${option.location}, ${option.country}`}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={t('createEvent.fields.raceday.selectRacetrack')}
                      required={formData.isRaceday}
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {loadingRacetracks ? <CircularProgress color="inherit" size={20} /> : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                />
              </Grid>
            )}
            
            <Grid item xs={12}>
              <TextField
                name="location"
                label={t('createEvent.fields.location')}
                fullWidth
                required
                value={formData.location}
                onChange={handleChange}
                disabled={formData.isRaceday && selectedRacetrack}
                helperText={formData.isRaceday && selectedRacetrack ? "Location is set from the selected racetrack" : ""}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                name="date"
                label={t('createEvent.fields.date')}
                type="date"
                fullWidth
                required
                value={formData.date}
                onChange={handleChange}
                InputLabelProps={{
                  shrink: true,
                }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                name="endDate"
                label={t('createEvent.fields.endDate')}
                type="date"
                fullWidth
                value={formData.endDate}
                onChange={handleChange}
                InputLabelProps={{
                  shrink: true,
                }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                name="price"
                label={t('createEvent.fields.price')}
                type="number"
                fullWidth
                value={formData.price}
                onChange={handleChange}
                inputProps={{ min: 0, step: 0.01 }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                name="maxParticipants"
                label={t('createEvent.fields.maxParticipants')}
                type="number"
                fullWidth
                value={formData.maxParticipants}
                onChange={handleChange}
                inputProps={{ min: 1 }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                name="signupLink"
                label={t('createEvent.fields.signupLink')}
                fullWidth
                value={formData.signupLink}
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.isPublic}
                    onChange={handleChange}
                    name="isPublic"
                    color="primary"
                  />
                }
                label={t('createEvent.fields.isPublic')}
              />
              <Typography variant="caption" color="text.secondary" display="block" mt={1}>
                {formData.isPublic 
                  ? t('eventDetail.publicEventNote') 
                  : t('eventDetail.privateEventNote')}
              </Typography>
            </Grid>
            
            {/* User selection for private events */}
            {!formData.isPublic && (
              <Grid item xs={12}>
                <Typography variant="subtitle2" gutterBottom>
                  {t('createEvent.fields.participants')}
                </Typography>
                <Autocomplete
                  multiple
                  id="participants-selection"
                  options={availableUsers}
                  loading={loadingUsers}
                  value={selectedUsers}
                  onChange={(event, newValue) => setSelectedUsers(newValue)}
                  getOptionLabel={(option) => option.name || option.username}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={t('createEvent.fields.selectParticipants')}
                      placeholder={t('createEvent.fields.selectParticipantsPlaceholder')}
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {loadingUsers ? <CircularProgress color="inherit" size={20} /> : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                  renderTags={(value, getTagProps) =>
                    value.map((option, index) => (
                      <Chip
                        label={option.name || option.username}
                        {...getTagProps({ index })}
                        key={option._id}
                      />
                    ))
                  }
                />
                <Typography variant="caption" color="text.secondary" display="block" mt={1}>
                  {t('createEvent.participantsNote')}
                </Typography>
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disabled={loading}>
            {t('common.cancel')}
          </Button>
          <Button 
            type="submit" 
            variant="contained" 
            color="primary" 
            disabled={loading || (formData.isRaceday && !selectedRacetrack)}
          >
            {loading ? <CircularProgress size={24} /> : t('common.create')}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default CreateEventModal; 